/**
 * Partner flow layers:
 * - isPartnerSession: any partner JWT (`jtoken`) → partner API (`/partner/web/user/*`)
 * - isPartnerWebviewSession: embed partner (token kept on navigations)
 * - isYeloAppSession: Yelo mobile app webview (`partner_source: yelo_app`) — pay in-webview
 */
export function isPartnerSession(): boolean {
  return !!localStorage.getItem("jtoken");
}

export const PARTNER_CHECKOUT_STORAGE_KEY = "PartnerCheckoutPayload";

export function getPartnerTokenFromStorage(): string {
  return (localStorage.getItem("jtoken") || "").replace(/^Bearer\s+/i, "");
}

/** Yelo app session token from register/authorize (`y1.` + encrypted payload). */
export const YELO_APP_COMPACT_TOKEN_PREFIX = "y1.";

export function isYeloAppCompactToken(token?: string): boolean {
  const raw = (token || getPartnerTokenFromStorage())
    .replace(/^Bearer\s+/i, "")
    .trim();
  return raw.startsWith(YELO_APP_COMPACT_TOKEN_PREFIX);
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const raw = token.replace(/^Bearer\s+/i, "").split(".")[1];
    if (!raw) return null;
    const json = atob(raw.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function jwtIndicatesPartnerEmbed(token: string): boolean {
  if (isYeloAppCompactToken(token)) return true;

  const decoded = decodeJwtPayload(token);
  if (!decoded) return false;

  const id = decoded.id as Record<string, unknown> | undefined;
  if (id?.partner_embed === true) return true;
  if (id?.partner_source === "yelo_app") return true;

  return decoded.partner_embed === true || decoded.partner_source === "yelo_app";
}

/** Yelo mobile app / partner webview session (not Jeeny redirect checkout). */
export function isYeloAppSession(): boolean {
  if (!isPartnerSession()) return false;
  if (isYeloAppCompactToken()) return true;
  const decoded = decodeJwtPayload(getPartnerTokenFromStorage());
  const id = decoded?.id as Record<string, unknown> | undefined;
  return id?.partner_source === "yelo_app";
}

/** Partner webview that keeps ?token= on navigations. */
export function isPartnerWebviewSession(): boolean {
  return isPartnerSession() && jwtIndicatesPartnerEmbed(getPartnerTokenFromStorage());
}

/** @deprecated Use isPartnerWebviewSession — kept for existing imports */
export function isYeloCheckoutSession(): boolean {
  return isPartnerWebviewSession();
}

/** @deprecated Use isPartnerWebviewSession */
export function isYeloCheckoutToken(token?: string): boolean {
  const raw = (token || getPartnerTokenFromStorage()).replace(/^Bearer\s+/i, "").trim();
  if (!raw) return false;
  return jwtIndicatesPartnerEmbed(raw);
}

/** Keep partner query params on navigations (token; embed/platform for Yelo app). */
export function withPartnerToken(path: string): string {
  if (!isPartnerWebviewSession()) return path;

  const token = getPartnerTokenFromStorage();
  if (!token) return path;

  const hashIndex = path.indexOf("#");
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
  const pathAndQuery = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = pathAndQuery.indexOf("?");
  const pathname = queryIndex >= 0 ? pathAndQuery.slice(0, queryIndex) : pathAndQuery;
  const query = queryIndex >= 0 ? pathAndQuery.slice(queryIndex + 1) : "";

  const params = new URLSearchParams(query);
  if (!params.has("token")) {
    params.set("token", token);
  }

  if (isYeloAppSession()) {
    if (!params.has("embed")) params.set("embed", "1");
    if (!params.has("platform")) params.set("platform", "app");
  }

  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hash}`;
}

export function partnerHomePath(): string {
  return withPartnerToken("/");
}

function appLanguagePrefix(): string {
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  return segment === "ar" || segment === "en" ? segment : "en";
}

/**
 * After 401 / logout: stay inside the Yelo app webview on Not authorised.
 * The token that was just rejected is deliberately not carried over — putting
 * it back in the URL re-stores it and loops straight into another 401. The app
 * re-authorises and reopens the webview with a fresh token.
 */
export function getPartnerUnauthorizedRedirect(): string {
  const lang = appLanguagePrefix();
  const params = new URLSearchParams({ embed: "1", platform: "app" });
  return `${window.location.origin}/${lang}/not-authorize?${params.toString()}`;
}
